import { GuestModel } from 'api-hooks/guest';
import { Value } from 'baseui/select';
import { PreferenceGroupSubTypeEnum } from 'common/constants/enum';
import { generateId } from 'common/utils/string';
import { useFormState } from 'components/elements/form';
import deleteColumn from 'components/elements/table/editable-columns/delete-column';
import TableInput from 'components/elements/table/table-input';
import {
  ActionType,
  ColumnDataType,
  IColumn,
} from 'components/elements/table/type';
import useGetGuestColumn from 'hooks/use-get-guest-column';
import useGetMultiplePreferenceColumn from 'hooks/use-get-multiple-preference-column';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';

import {
  CruiseSaleCruiseSaleGuestFormType,
  CruiseSaleFormType,
} from '../form-type';
import {
  addNewDetailsItem,
  addNewGuestItem,
} from '../hooks/use-get-default-values';

interface Props {
  parentIndex: number;
}

function tagTransformer(item: { id: string; count?: number; name: string }) {
  return {
    id: item.id,
    label: `${item.name} ${item.count ? `(X${item.count})` : ''}`,
  };
}

function useCruiseGuestTableList(): IColumn<CruiseSaleCruiseSaleGuestFormType>[] {
  const { t } = useTranslation();
  const guestColumn = useGetGuestColumn();
  const { editable } = useFormState();

  const multiplePreferenceColumn = useGetMultiplePreferenceColumn({
    subType: PreferenceGroupSubTypeEnum.Cruise,
  });

  return [
    {
      header: t('common:guest'),
      accessorKey: 'guestId',
      minSize: 250,
      dataType: ColumnDataType.select,
      required: true,
      selectProps: guestColumn,
    },
    {
      header: t('common:guest_preferences'),
      accessorKey: 'guestPreferences',
      minSize: 300,
      dataType: ColumnDataType.tags,
      tagKeyName: 'label',
      maxTags: 2,
    },
    {
      header: t('guest:booking_preferences'),
      accessorKey: 'preferences',
      minSize: 250,
      dataType: ColumnDataType.selectMulti,
      selectProps: multiplePreferenceColumn,
    },
    ...deleteColumn(editable),
  ];
}

export default function CruiseGuestTable(props: Props) {
  const { editable } = useFormState();
  const name = `cruiseSales.${props.parentIndex}.cruiseSaleGuests` as const;
  const detailsName =
    `cruiseSales.${props.parentIndex}.cruiseSaleDetails` as const;

  const { control, trigger, getValues, setValue, clearErrors } =
    useFormContext<CruiseSaleFormType>();

  const fields = getValues(name);

  const { append, remove, update } = useFieldArray({
    name,
    control,
    keyName: '__customId',
  });

  const columns = useCruiseGuestTableList();

  const dispatch = React.useCallback(
    (action) => {
      const columnId =
        action.columnId as keyof CruiseSaleCruiseSaleGuestFormType;
      const type = action.type as ActionType;
      const value = action.value;
      const extra = action.extra as Value;
      const currentRowIndex = action.rowIndex as number;
      const guests = getValues(name);
      const guest = guests[currentRowIndex];
      const details = getValues(detailsName);
      const isGroupedPrice = getValues(
        `cruiseSales.${props.parentIndex}.isGroupedPrice`,
      );

      const otherVal: () => Partial<CruiseSaleCruiseSaleGuestFormType> = () => {
        if (columnId === 'guestId') {
          const _guest = extra?.[0]?.extra as GuestModel;
          const guestPreferences = [
            ..._guest.preferenceStats,
            ..._guest.preferences,
          ].map((preference) => tagTransformer(preference));
          return {
            guestName: extra?.[0]?.label as string,
            guestPreferences,
          };
        }
        return {};
      };

      if (type === 'addRow') {
        append(addNewGuestItem());

        if (!isGroupedPrice) {
          setValue(detailsName, [...details, addNewDetailsItem()]);
        }
        trigger(name);
      } else if (type === 'removeRow') {
        remove(currentRowIndex);

        if (!isGroupedPrice) {
          setValue(
            detailsName,
            details.filter((_, index) => index !== currentRowIndex),
          );
        }
        trigger(name);
      } else if (type === 'update_cell') {
        update(currentRowIndex, {
          ...guest,
          [columnId]: value,
          guestDetailId: guest.guestDetailId || generateId(),
          ...otherVal(),
        });

        details.forEach((row, index) => {
          if (row.guestDetailId === guest.guestDetailId) {
            setValue(`${detailsName}.${index}`, {
              ...row,
              guestId: getValues(name)[currentRowIndex].guestId,
              guestDetailId: getValues(name)[currentRowIndex].guestDetailId,
              ...otherVal(),
            });
          }
        });
        clearErrors(name);
      }
    },
    [
      append,
      clearErrors,
      detailsName,
      getValues,
      name,
      props.parentIndex,
      remove,
      setValue,
      trigger,
      update,
    ],
  );

  return (
    <div style={{ width: '100%' }}>
      <TableInput
        columns={columns}
        data={fields}
        dispatch={dispatch}
        disabled={!editable}
        name={name}
        uniqueRowKey="customId"
      />
    </div>
  );
}
